import { ClientAppPermission, PropertiesPermission } from '@idc-forms/permissions'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { mobileAccessV2Service } from '../services/mobile-access-v2.service'

/**
 * El acceso móvil de una negociación (ADR 0024), en sus dos mitades.
 *
 * La LECTURA se pide sola al abrir el expediente y no pide MFA. La ESCRITURA la disparan
 * los dos gatillos —el checkbox al aprobar y el botón— y sí la pide, porque va hacia
 * afuera: abre una cuenta y termina en el correo de una persona real.
 */
function useV2AccessToken() {
  const { auth } = useAuth()
  return auth?.v2AccessToken ?? ''
}

/**
 * Quién puede liberar el acceso. Espeja el `@V2Policy` del endpoint —los DOS permisos, no
 * cualquiera de ellos— para que la pantalla no ofrezca un botón que el backend va a
 * rechazar con 403. La comprobación real sigue siendo la del servidor; ésta sólo evita
 * prometer.
 */
export function useCanReleaseMobileAccess() {
  const { hasAll } = usePermissions()
  return hasAll([ClientAppPermission.ManageClientAppAccess, PropertiesPermission.ReadProperties])
}

/** Quién puede revocar un acceso o bloquear desde acá. Espeja los `@V2Policy` de sus rutas. */
export function useCanManageClientAppAccess() {
  const { has } = usePermissions()
  return has(ClientAppPermission.ManageClientAppAccess)
}

export function useCanBlockClientAppAccount() {
  const { has } = usePermissions()
  return has(ClientAppPermission.BlockClientAppAccount)
}

export function useMobileAccessStatusQuery(publicId: string | null, enabled = true) {
  const accessToken = useV2AccessToken()

  return useQuery({
    queryKey: queryKeys.negotiations.mobileAccess(publicId),
    queryFn: async () => await mobileAccessV2Service.status(accessToken, publicId ?? ''),
    // Sin `neg_` no hay a qué preguntarle, y sin token v2 la sesión entró por el carril
    // legacy: en los dos casos la pantalla no muestra nada en vez de tirar un 401.
    enabled: enabled && Boolean(publicId) && Boolean(accessToken),
    refetchOnWindowFocus: false,
    // Un 403 es determinista: reintentarlo sólo retrasa el aviso.
    retry: false
  })
}

export function useReleaseMobileAccessMutation(publicId: string | null) {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()

  return useMutation({
    /**
     * La variable es el `cli_` del dueño a habilitar, y es OPCIONAL: `mutate()` sin nada
     * corre la liberación automática —lo que hace el botón grande— y `mutate('cli_…')`
     * habilita a esa persona salteando la política (decisión Nº 28).
     */
    mutationFn: async (ownerId?: string) =>
      await mobileAccessV2Service.release(accessToken, publicId ?? '', ownerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.negotiations.mobileAccess(publicId)
      })
    }
  })
}
